import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './hooks';
import { AppShell } from '../components/layout/AppShell';
import { PageTransition } from '../components/ui/PageTransition';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { RoleRoute } from '../components/auth/RoleRoute';
import { ToastViewport, pushToast } from '../components/ui/ToastViewport';
import { LandingPage } from '../pages/LandingPage';
import { DashboardPage } from '../pages/DashboardPage';
import { DonationFlowPage } from '../pages/DonationFlowPage';
import { MapExperiencePage } from '../pages/MapExperiencePage';
import { NgoPanelPage } from '../pages/NgoPanelPage';
import { AnalyticsPage } from '../pages/AnalyticsPage';
import { AdminPage } from '../pages/AdminPage';
import { LoginPage } from '../pages/LoginPage';
import { DeliveriesPage } from '../pages/DeliveriesPage';
import { DriverPanelPage } from '../pages/DriverPanelPage';
import { DriversPage } from '../pages/DriversPage';
import { ControlRoomPage } from '../pages/ControlRoomPage';
import { logout, markActivity, restoreSession } from '../features/auth/authSlice';
const WorkspaceRoutes = () => (_jsx(ProtectedRoute, { children: _jsx(AppShell, { children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(DashboardPage, {}) }), _jsx(Route, { path: "/donations", element: _jsx(RoleRoute, { roles: ['ADMIN'], children: _jsx(DonationFlowPage, {}) }) }), _jsx(Route, { path: "/map", element: _jsx(RoleRoute, { roles: ['VOLUNTEER', 'ADMIN'], children: _jsx(MapExperiencePage, {}) }) }), _jsx(Route, { path: "/ngo", element: _jsx(RoleRoute, { roles: ['ADMIN'], children: _jsx(NgoPanelPage, {}) }) }), _jsx(Route, { path: "/deliveries", element: _jsx(RoleRoute, { roles: ['ADMIN'], children: _jsx(DeliveriesPage, {}) }) }), _jsx(Route, { path: "/drivers", element: _jsx(RoleRoute, { roles: ['ADMIN'], children: _jsx(DriversPage, {}) }) }), _jsx(Route, { path: "/control-room", element: _jsx(RoleRoute, { roles: ['ADMIN'], children: _jsx(ControlRoomPage, {}) }) }), _jsx(Route, { path: "/driver", element: _jsx(RoleRoute, { roles: ['VOLUNTEER'], children: _jsx(DriverPanelPage, {}) }) }), _jsx(Route, { path: "/analytics", element: _jsx(RoleRoute, { roles: ['ADMIN'], children: _jsx(AnalyticsPage, {}) }) }), _jsx(Route, { path: "/admin", element: _jsx(RoleRoute, { roles: ['ADMIN'], children: _jsx(AdminPage, {}) }) })] }) }) }));
export const App = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { accessToken, initialized, lastActivityAt, user } = useAppSelector((state) => state.auth);
    useEffect(() => {
        dispatch(restoreSession());
    }, [dispatch]);
    useEffect(() => {
        const handler = () => dispatch(markActivity());
        window.addEventListener('pointerdown', handler);
        window.addEventListener('keydown', handler);
        return () => {
            window.removeEventListener('pointerdown', handler);
            window.removeEventListener('keydown', handler);
        };
    }, [dispatch]);
    useEffect(() => {
        if (!initialized || !user || !lastActivityAt) {
            return;
        }
        const idleTimer = window.setInterval(() => {
            if (Date.now() - lastActivityAt > 30 * 60 * 1000) {
                dispatch(logout());
                pushToast('You were logged out after inactivity.', 'success');
            }
        }, 60000);
        return () => window.clearInterval(idleTimer);
    }, [dispatch, initialized, lastActivityAt, user]);
    useEffect(() => {
        if (!accessToken || !user) {
            return;
        }
        let payload = {};
        try {
            payload = JSON.parse(atob(accessToken.split('.')[1] ?? ''));
        }
        catch {
            payload = {};
        }
        const expiresAt = typeof payload.exp === 'number' ? payload.exp * 1000 : Date.now();
        const timeout = window.setTimeout(() => {
            dispatch(logout());
            pushToast('Your session expired. Please log in again.', 'error');
        }, Math.max(expiresAt - Date.now(), 0));
        return () => window.clearTimeout(timeout);
    }, [accessToken, dispatch, user]);
    return (_jsxs(_Fragment, { children: [_jsx(ToastViewport, {}), _jsx(AnimatePresence, { mode: "wait", children: _jsxs(Routes, { location: location, children: [_jsx(Route, { path: "/", element: _jsx(PageTransition, { children: _jsx(LandingPage, {}) }) }), _jsx(Route, { path: "/login", element: _jsx(PageTransition, { children: _jsx(LoginPage, {}) }) }), _jsx(Route, { path: "/app/*", element: _jsx(PageTransition, { children: _jsx(WorkspaceRoutes, {}) }) })] }, location.pathname) })] }));
};
